import { useState } from "react";

interface PasswordFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  id: string;
  label?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

/**
 * Campo de senha com alternância de visibilidade acionada enquanto pressionado (hold-to-reveal).
 * A senha só fica legível enquanto o botão do mouse ou toque na tela estiver mantido pressionado.
 */
export function PasswordField({
  id,
  label = "Senha",
  value,
  onChange,
  className,
  ...inputProps
}: PasswordFieldProps) {
  const [isVisible, setIsVisible] = useState(false);

  function startReveal(e: React.SyntheticEvent) {
    // Evita que o clique retire o foco ou envie formulário
    e.preventDefault();
    setIsVisible(true);
  }

  function stopReveal() {
    setIsVisible(false);
  }

  return (
    <div className={`field ${className ?? ""}`.trim()}>
      {label && <label htmlFor={id}>{label}</label>}
      <div className="password-input-wrapper">
        <input
          {...inputProps}
          id={id}
          type={isVisible ? "text" : "password"}
          value={value}
          onChange={onChange}
        />
        <button
          type="button"
          className={`password-toggle-btn ${isVisible ? "is-active" : ""}`}
          aria-label="Pressione e segure para visualizar a senha"
          title="Pressione e segure para ver a senha"
          tabIndex={0}
          onMouseDown={startReveal}
          onMouseUp={stopReveal}
          onMouseLeave={stopReveal}
          onTouchStart={startReveal}
          onTouchEnd={stopReveal}
          onTouchCancel={stopReveal}
          onKeyDown={(e) => {
            if (e.key === " " || e.key === "Enter") {
              e.preventDefault();
              setIsVisible(true);
            }
          }}
          onKeyUp={() => setIsVisible(false)}
          onBlur={stopReveal}
        >
          {isVisible ? (
            // Olho aberto preenchido (visível enquanto pressionado)
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
              <circle cx="12" cy="12" r="3" fill="currentColor" />
            </svg>
          ) : (
            // Olho com traço / olho padrão (oculto)
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
}
